import { NotNestedValues, SupportPolicy } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { isNullish } from 'remeda';
import { SupportPolicyFormValues } from 'src/components/Assistant/policies-tab/policy-form';
import { serializeSupportPolicyConditions } from 'src/graphql/entities/support-policy/support-policy.conditions';
import {
  CREATE_SUPPORT_POLICY_MUTATION,
  GET_SUPPORT_POLICY_QUERY,
} from 'src/graphql/entities/support-policy/support-policy.operations';
import { buildSupportPolicyPartnerFilterValue } from 'src/graphql/entities/support-policy/support-policy.utils';
import { useNotifications } from 'src/utils/hooks';
import { GetSupportPolicyQuery } from './use-support-policy';

export interface CreateSupportPolicyMutation {
  createSupportPolicyWithGroups: NotNestedValues<SupportPolicy>;
}

export type CreateSupportPolicyParams = {
  supportPolicy: Omit<
    Pick<
      SupportPolicy,
      | 'title'
      | 'type'
      | 'category'
      | 'description'
      | 'createdBy'
      | 'partnerId'
      | 'addressIds'
      | 'clientIds'
      | 'clientToAddressIds'
      | 'conditions'
      | 'urgencyLevel'
      | 'escalationRules'
    >,
    'urgencyLevel' | 'escalationRules'
  > & {
    urgencyLevel?: string | null;
    escalationRules?: string | null;
    validUntil: string | undefined | null;
    pinnedAt: string | undefined | null;
  };
  userGroupIds: string[];
};

export type CreateSupportPolicyMutationParams = {
  data: CreateSupportPolicyParams;
};

export const useCreateSupportPolicy = () => {
  const { showErrorAlert } = useNotifications();

  const [createSupportPolicyMutation, { loading: isCreating }] = useMutation<
    CreateSupportPolicyMutation,
    CreateSupportPolicyMutationParams
  >(CREATE_SUPPORT_POLICY_MUTATION, {
    update(cache, { data }) {
      const created = data?.createSupportPolicyWithGroups;
      if (!created) return;

      const partnerId = created.partnerId;

      if (isNullish(partnerId)) return;
      const partnerFilterValue = buildSupportPolicyPartnerFilterValue(partnerId);

      cache.updateQuery<GetSupportPolicyQuery>(
        {
          query: GET_SUPPORT_POLICY_QUERY,
          variables: {
            params: {
              sorting: [
                { attribute: 'pinnedAt', desc: true },
                { attribute: 'createdAt', desc: true },
              ],
              filters: [
                {
                  attribute: 'partnerId',
                  operator: 'in',
                  value: partnerFilterValue,
                },
              ],
            },
          },
        },
        (existing): GetSupportPolicyQuery | null => {
          if (!existing) return existing;

          const alreadyExists = existing.getSupportPolicy.records?.some(
            (record) => record.id === created.id,
          );

          if (alreadyExists) return existing;

          return {
            ...existing,
            getSupportPolicy: {
              ...existing.getSupportPolicy,
              records: [
                ...(existing.getSupportPolicy.records ?? []),
                created as SupportPolicy,
              ],
              metadata: {
                ...existing.getSupportPolicy.metadata,
                totalRecords: (existing.getSupportPolicy.metadata?.totalRecords ?? 0) + 1,
              },
            },
          };
        },
      );
    },
    // @ts-expect-error Generated type mismatch for pinnedAt column
    optimisticResponse: (input) => {
      const tempId = `temp-id-${crypto.randomUUID()}`;
      return {
        createSupportPolicyWithGroups: {
          __typename: 'SupportPolicy',
          id: tempId,
          supportPolicyId: tempId,
          type: input.data.supportPolicy.type,
          category: input.data.supportPolicy.category,
          description: input.data.supportPolicy.description,
          pinnedAt: input.data.supportPolicy.pinnedAt ? new Date().toISOString() : null,
          archivedAt: null,
          validUntil: input.data.supportPolicy.validUntil,
          title: input.data.supportPolicy.title,
          createdBy: input.data.supportPolicy.createdBy,
          partnerId: input.data.supportPolicy.partnerId,
          addressIds: input.data.supportPolicy.addressIds,
          clientIds: input.data.supportPolicy.clientIds,
          clientToAddressIds: input.data.supportPolicy.clientToAddressIds,
          conditions: input.data.supportPolicy.conditions,
          urgencyLevel: input.data.supportPolicy.urgencyLevel ?? null,
          escalationRules: input.data.supportPolicy.escalationRules ?? null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      };
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to create policy, please try again');
    },
  });

  const createSupportPolicy = useCallback(
    async ({
      title,
      type,
      category,
      description,
      validUntil,
      createdBy,
      partnerId,
      pinnedAt,
      userGroups = [],
      addressIds = [],
      clientIds = [],
      clientToAddressIds = [],
      conditions,
      enableConditions,
      urgencyLevel,
      escalationRules,
    }: SupportPolicyFormValues & { createdBy: string; partnerId: number }) => {
      await createSupportPolicyMutation({
        variables: {
          data: {
            supportPolicy: {
              title,
              type,
              category,
              description,
              pinnedAt: pinnedAt ? new Date().toISOString() : null,
              validUntil: validUntil ? validUntil.toISOString() : null,
              createdBy,
              partnerId,
              addressIds: JSON.stringify(addressIds ?? []),
              clientIds: JSON.stringify(clientIds ?? []),
              clientToAddressIds: JSON.stringify(clientToAddressIds ?? []),
              conditions:
                serializeSupportPolicyConditions(conditions, !!enableConditions) ?? '',
              urgencyLevel: urgencyLevel ?? null,
              escalationRules: escalationRules ?? null,
            },
            userGroupIds: userGroups || [],
          },
        },
      });
    },
    [createSupportPolicyMutation],
  );

  return {
    createSupportPolicy,
    isCreating,
  };
};
